import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Input,
  InputProps,
  Image,
  SimpleGrid,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FiPackage } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import type { GoLiveWindowDto, ProductDefault, SelectedProduct } from '@foodfluence/shared';
import {
  getCampaignPickLimit,
  getChosenProductValue,
  getProductVariants,
  getVariantChoices,
  getProductSelectionCatalog,
  getCampaignProducts,
  getOptionalProducts,
  getRequiredProducts,
  sumProductValues,
  validateProductSelection,
  isDeliveryOnlyUgc,
} from '@foodfluence/shared';
import { collabApi } from 'services/api/collab';
import { acceptCollabNotification, sendAddressSubmissionEmail } from 'services/flaskService';
import AddressFormFields, { type AddressFormData } from 'components/AddressFormFields';
import LocationSearchBox from '../scheduling/components/LocationSearchBox';
import PostingDateSelector from '../components/PostingDateSelector';
import { useCampaignLocations } from 'hooks/useCampaignLocations';
import { formatDateToYYYYMMDD, formatPostingDate } from 'utils/dateUtils';
import { getInfluencerPrimaryHandle, isContactInfoValid } from 'utils/influencerUtils';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
// The in-store scheduling wizard owns the chrome this flow is meant to match. Imported in place
// rather than moved, so the scheduling flow keeps working untouched.
import WizardHeader from '../scheduling/components/WizardHeader';
import WizardBottomBar from '../scheduling/components/WizardBottomBar';
import CollabConfirmedScreen from '../scheduling/components/CollabConfirmedScreen';
import { eyebrowProps, stepTitleProps, wizardTokens } from '../scheduling/styles';
import { parsePhone } from 'features/profile/utils';
import ProductPickerCard from './ProductPickerCard';
import type { SelectProductsDestination as Destination } from '../utils/productSelection';
import { addPick, productSelectionDestination, productsToReceive } from '../utils/productSelection';
import { submissionPathForCollab } from '../utils/collabRoutes';
import ProductSelectionSummary from './ProductSelectionSummary';
import PostByConfirm, { getGoLiveConfirmation, usePostByConfirm } from '../components/PostByConfirm';

// Declared once, in the util that decides it — two copies of this union is how Shopify ended up
// routable in one file and not the other.
export type { SelectProductsDestination } from '../utils/productSelection';

interface SelectProductsState {
  collabId: string;
  destination: Destination;
  /** The go-live window confirmed on the review page (see getGoLiveConfirmation). */
  goLiveConfirmed?: GoLiveWindowDto;
  /** Where to return for the shipping-address step. */
}

const formatUsd = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

/**
 * Products the brand sends every creator. Not a choice — but a creator should still see what is
 * coming, not just what they picked.
 */
const IncludedAnyway: React.FC<{
  products: ProductDefault[];
  /** Which variant each product resolves to — a sole variant is recorded without being asked. */
  variantByProduct: Record<string, string>;
}> = ({ products, variantByProduct }) => {
  if (!products.length) return null;
  return (
    <Box pt="16px">
      <Text as="h2" m={0} mb="10px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" color="#66666A">
        INCLUDED FOR YOU EITHER WAY
      </Text>
      <Box border="1px solid #E4E4E0" borderRadius="18px" px="14px">
        {products.map((product, i) => {
          // Resolved from ALL variants, not the ones offered as a choice: a product whose single
          // variant is priced differently from the product would otherwise be listed at the base
          // price, which is not what the creator receives or the brand pays.
          const chosen = getProductVariants(product).find(
            (v) => v.id === variantByProduct[product.id]
          );
          const src = chosen?.imageUrl ?? product.imageUrl;
          return (
            <HStack key={product.id} spacing="12px" py="10px" borderTop={i ? '1px solid #ECECEA' : undefined}>
              <Flex w="44px" h="44px" flexShrink={0} borderRadius="12px" bg="#F2F2F0" align="center" justify="center" overflow="hidden" color="#9A9A9E">
                {src ? <Image src={src} alt="" w="100%" h="100%" objectFit="cover" /> : <FiPackage size={18} aria-hidden />}
              </Flex>
              <Box flex="1" minW={0}>
                <Text fontSize="15px" fontWeight={600} lineHeight="1.3" color={wizardTokens.ink} noOfLines={2}>
                  {product.name}
                </Text>
                <Text fontSize="13px" color="#55555A" lineHeight="normal">
                  {chosen ? `${chosen.name} · ` : ''}
                  {formatUsd(chosen?.productValue ?? product.productValue)} value
                </Text>
              </Box>
            </HStack>
          );
        })}
      </Box>
    </Box>
  );
};

// Board PickProducts inputs: 48px, 14px radius, 16px text.
const FIELD: InputProps = {
  h: '48px',
  px: '14px',
  borderRadius: '14px',
  border: '1px solid #DCDCD8',
  bg: '#FFFFFF',
  fontSize: '16px',
  color: '#0E0E0E',
  _placeholder: { color: '#9A9A9E' },
  _hover: { borderColor: '#DCDCD8' },
  _focusVisible: { borderColor: '#0E0E0E', boxShadow: 'none' },
};

/**
 * The creator chooses their products before accepting, one group per step. Styled on the in-store
 * scheduling wizard so the two accept flows feel like the same product.
 */
const SelectProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const state = (location.state ?? {}) as Partial<SelectProductsState>;
  const collabId = state.collabId;

  const collabs = useContext(CollabDataContext);
  // Keyed by collab id, not campaign id.
  const campaignsByCollabId = useContext(InfluencerCampaignDataContext);

  const collab = useMemo(
    () => collabs.find((c) => c.id === collabId),
    [collabs, collabId]
  );
  const campaign = collabId ? campaignsByCollabId[collabId] : undefined;
  // Go-live campaigns: an accept sends the window ticked on the review page. A refusal shows the
  // checkbox here with the new dates, so the creator re-confirms without losing their picks.
  const goLive = usePostByConfirm(
    collab?.status === 'pendingInfluencerApproval' ? campaign : null,
    collab,
    collabId ? getGoLiveConfirmation(collabId, state) : undefined
  );
  const clients = useContext(ClientDataContext);
  const client = campaign?.clientID ? clients[campaign.clientID] : undefined;
  const { influencerData } = useContext(InfluencerDataContext);

  // Same source the scheduling page uses: a collab with proposed locations is limited to those,
  // otherwise the campaign's own.
  const { locations: retailLocations, loading: retailLocationsLoading } = useCampaignLocations(
    collab?.proposedSelectedLocationIds?.length
      ? collab.proposedSelectedLocationIds
      : campaign?.foodfluenceLocations
  );

  const offeredProducts = useMemo(() => getCampaignProducts(campaign), [campaign]);
  const requiredProducts = useMemo(() => getRequiredProducts(offeredProducts), [offeredProducts]);
  const optionalProducts = useMemo(() => getOptionalProducts(offeredProducts), [offeredProducts]);
  const pickLimit = useMemo(() => getCampaignPickLimit(campaign), [campaign]);
  // A campaign whose creators receive everything has no product step at all.
  const offersChoice = optionalProducts.length > pickLimit;

  const [stepIndex, setStepIndex] = useState(0);
  const [picked, setPicked] = useState<string[]>([]);
  // Which variant each product is taken in — required products included, since they need a size
  // too. Kept apart from `picked` so the allowance keeps counting products only.
  const [variantByProduct, setVariantByProduct] = useState<Record<string, string>>({});
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [addressData, setAddressData] = useState<AddressFormData | null>(null);
  // Retail collects where the creator is picking up and when they plan to post, in the wizard
  // rather than on a page afterwards.
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [postingDate, setPostingDate] = useState<string | null>(null);
  // Shopify's draft order carries the recipient, which ship-to-home takes from the profile.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set once the accept lands: the wizard hands over to the confirmation screen rather than
  // bouncing straight to the collab, so the creator sees what they just agreed to.
  const [isConfirmed, setIsConfirmed] = useState(false);

  // The campaign decides which accept this is, not the router state. `state.destination` is
  // history — a stale entry, a back button or a hand-written push can carry a value that no longer
  // matches the campaign, and the final `else` below accepts as brand-managed, which asks for no
  // address at all. A ship-to-home collab arriving with `destination: 'brandManaged'` would then be
  // accepted and funded with nothing to ship to. Fall back to the router only while the campaign
  // is still loading, when there is nothing better to go on.
  const destination = productSelectionDestination(campaign) ?? state.destination;

  // Ship-to-home needs a delivery address, and it belongs in the wizard rather than in a modal
  // afterwards — the in-store flow collects its location the same way.
  const isShopify = destination === 'shopify';
  // Shopify ships too, and its draft order additionally needs a name, phone and email.
  const needsAddress = destination === 'shipping' || isShopify;
  // Retail is picked up in person, so it asks where and when instead of for an address.
  const needsSchedule = destination === 'retail';
  // A campaign can include every product and still ask a question: which size, which colour. That
  // earns the product step just as a real pick does — it is the only screen that asks.
  const needsVariantChoice = useMemo(
    () => offeredProducts.some((product) => getVariantChoices(product).length > 0),
    [offeredProducts]
  );
  // Every campaign that reaches the wizard shows its products, whether or not anything about them
  // is up to the creator: a step they read is better than products they only meet at the confirm.
  const hasProductStep = offeredProducts.length > 0;
  // What the creator gets regardless of what they pick. With no choice between products, that is
  // everything on offer.
  const includedProducts = offersChoice ? requiredProducts : offeredProducts;
  const productStepCount = hasProductStep ? 1 : 0;
  // Address and schedule are the same slot: no destination asks for both.
  const detailStepCount = needsAddress || needsSchedule ? 1 : 0;
  const addressStepIndex = needsAddress ? productStepCount : -1;
  const scheduleStepIndex = needsSchedule ? productStepCount : -1;
  const reviewStepIndex = productStepCount + detailStepCount;
  const totalSteps = reviewStepIndex + 1;
  const isProductStep = hasProductStep && stepIndex === 0;
  const isAddressStep = stepIndex === addressStepIndex;
  const isScheduleStep = stepIndex === scheduleStepIndex;
  const isReviewStep = stepIndex === reviewStepIndex;

  // Required products come whatever happens; the picked ones are chosen from the optional set —
  // and when the allowance covers every optional product, all of them come.
  const chosenProducts = useMemo(
    () => productsToReceive(offeredProducts, picked, pickLimit),
    [offeredProducts, picked, pickLimit]
  );

  // What gets recorded: one entry per chosen PRODUCT, named and priced at its variant. Flat, with
  // a top-level productValue — the Python charge sums it with raw dict lookups.
  const selectedProducts = useMemo<SelectedProduct[]>(
    () =>
      chosenProducts.map((product) => {
        const variantId = variantByProduct[product.id];
        const variant = getProductVariants(product).find((v) => v.id === variantId);
        const imageUrl = variant?.imageUrl ?? product.imageUrl;
        return {
          id: product.id,
          name: product.name,
          productValue: getChosenProductValue(product, variantId),
          ...(imageUrl ? { imageUrl } : {}),
          ...(variant ? { variantId: variant.id, variantName: variant.name } : {}),
        };
      }),
    [chosenProducts, variantByProduct]
  );

  /** Every product still owing a variant choice — required ones included. */
  const missingVariant = useMemo(
    () =>
      chosenProducts.filter(
        (product) => getVariantChoices(product).length > 0 && !variantByProduct[product.id]
      ),
    [chosenProducts, variantByProduct]
  );

  const runningTotal = sumProductValues(selectedProducts);

  // The address exactly as it will be stored. Same assembly the shipping modal used, hoisted out
  // of `submit` so the review and confirmation screens show what is actually sent — displaying
  // `addressData.address` there dropped the apartment or unit number.
  const fullAddress = useMemo(() => {
    if (!addressData?.address) return undefined;
    const address2 =
      `${addressData.secondaryAddressType ?? ''} ${addressData.secondaryAddressNumber ?? ''}`.trim();
    if (!address2) return addressData.address;
    return `${addressData.address.split(',')[0]}, ${address2},${addressData.address.substring(
      addressData.address.indexOf(',') + 1
    )}`;
  }, [addressData]);

  // A collab the brand accepted first already carries the schedule its creator gave at acceptance,
  // and reaches this wizard only to settle a product — a catalog added to a live campaign owes one
  // from collabs already past the point of choosing. Asking for the store and the date again from
  // blank is what a creator sees as being sent back to re-schedule instead of on to upload, and
  // when the posting window has since lapsed there is no date left to pick at all. Seed from the
  // collab, once, so anything the creator changes afterwards stands.
  const scheduleSeeded = useRef(false);
  useEffect(() => {
    if (scheduleSeeded.current || !needsSchedule || !collab) return;
    if (!collab.scheduledPostingDate && !collab.selectedLocation) return;
    scheduleSeeded.current = true;
    if (collab.scheduledPostingDate) setPostingDate(collab.scheduledPostingDate);
    if (collab.selectedLocation) setSelectedLocation(collab.selectedLocation);
  }, [needsSchedule, collab]);

  // The search box shows an address, which only exists once the campaign's locations land — after
  // the seed above. Once, for the same reason: a creator clearing the box to search again must not
  // have the old address typed back underneath them.
  const addressSeeded = useRef(false);
  useEffect(() => {
    if (addressSeeded.current || !selectedLocation || !retailLocations.length) return;
    const seeded = retailLocations.find((loc) => loc.docId === selectedLocation);
    if (!seeded?.fullAddress) return;
    addressSeeded.current = true;
    setLocationSearchQuery(seeded.fullAddress);
  }, [retailLocations, selectedLocation]);

  useEffect(() => {
    if (!isShopify) return;
    const known = (value?: string) =>
      value && value.toLowerCase() !== 'unknown' ? value : '';
    setFirstName(known(influencerData?.firstName));
    setLastName(known(influencerData?.lastName));
    if (influencerData?.phoneNumber) {
      const parsed = parsePhone(influencerData.phoneNumber);
      setCountryCode(parsed.countryCode);
      setPhoneNumber(parsed.nationalNumber);
    }
    if (influencerData?.email_address) setEmail(influencerData.email_address);
  }, [isShopify, influencerData]);

  // A product with a single variant decides itself. Record it rather than asking the creator a
  // question with one answer — the draft order still needs a variant id.
  useEffect(() => {
    const soleVariants = offeredProducts
      .map((product) => [product.id, getProductVariants(product)] as const)
      .filter(([, variants]) => variants.length === 1);
    if (!soleVariants.length) return;
    setVariantByProduct((prev) => {
      let changed = false;
      const next = { ...prev };
      soleVariants.forEach(([productId, variants]) => {
        if (next[productId] !== variants[0].id) {
          next[productId] = variants[0].id;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [offeredProducts]);

  const toggle = (productId: string) => {
    setPicked((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : addPick(prev, productId, pickLimit)
    );
  };

  /**
   * Ticking a variant is how a product with options gets picked.
   *
   * One variant per product: ticking a different one MOVES the pick rather than adding an item,
   * and ticking the one already held gives the product back. Required products can have their
   * variant changed but never leave the selection.
   */
  const chooseVariant = (productId: string, variantId: string) => {
    const isRequired = requiredProducts.some((p) => p.id === productId);
    const alreadyChosen = variantByProduct[productId] === variantId;

    if (alreadyChosen && !isRequired) {
      setPicked((prev) => prev.filter((id) => id !== productId));
      setVariantByProduct((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      return;
    }
    if (alreadyChosen) return;

    if (!isRequired && !picked.includes(productId)) {
      const next = addPick(picked, productId, pickLimit);
      // Only when the campaign hands out nothing optional is there no pick to take.
      if (next === picked) return;
      setPicked(next);
    }
    setVariantByProduct((prev) => ({ ...prev, [productId]: variantId }));
  };

  const atLimit = offersChoice && picked.length >= pickLimit;
  const hasValidContact =
    !isShopify || isContactInfoValid(firstName, lastName, email, phoneNumber, countryCode);
  const hasValidAddress = !!addressData?.isAddressValidated && hasValidContact;
  // Every pick must also have settled its variant, required products included.
  const selectionComplete =
    (!offersChoice || picked.length === pickLimit) && missingVariant.length === 0;
  const pickupAddress = retailLocations.find((loc) => loc.docId === selectedLocation)?.fullAddress;
  const summaryDestination = needsSchedule
    ? {
        shippingAddress: pickupAddress,
        addressLabel: 'PICKING UP AT',
        editAddressLabel: 'your pickup location',
        postingDate: postingDate ? formatPostingDate(postingDate) : undefined,
        deliveryOnly: isDeliveryOnlyUgc(collab, campaign),
      }
    : { shippingAddress: needsAddress ? fullAddress : undefined };

  // A campaign with no locations has nothing to pick from, so only the date is owed.
  // `retailLocations` is empty while it loads, which is indistinguishable from "this campaign has
  // no locations" — and that let a creator advance past the schedule step without choosing one.
  // Nothing is known about whether a location is needed until the fetch settles.
  // A location is always required, never merely when the list happens to be non-empty. The retail
  // controller rejects a missing `selectedLocation` with a 400, so treating an empty list as valid
  // let the creator through the step and into an error they could do nothing about. An empty list
  // is a campaign that has no pickup location configured — surfaced below as a message rather than
  // as a Continue button that fails.
  const hasValidSchedule = !!postingDate && !retailLocationsLoading && !!selectedLocation;
  const canAdvance = isReviewStep
    ? (!needsAddress || hasValidAddress) &&
      (!needsSchedule || hasValidSchedule) &&
      !goLive.blockedReason &&
      selectionComplete
    : isAddressStep
      ? hasValidAddress
      : isScheduleStep
        ? hasValidSchedule
        : selectionComplete;

  const goBack = () => {
    if (stepIndex === 0) {
      navigate(-1);
      return;
    }
    setStepIndex((i) => i - 1);
  };

  const submit = async () => {
    if (!collabId) return;
    // Validated against the groups, exactly as the server does; the selection itself stays flat.
    const check = validateProductSelection(
      getProductSelectionCatalog(campaign),
      selectedProducts.map((p) => ({ productId: p.id, variantId: p.variantId }))
    );
    // `=== false` rather than `!check.ok`: this app builds with `strictNullChecks: false`
    // (tsconfig.json), under which truthiness does not narrow a discriminated union.
    if (check.ok === false) {
      toast({ title: check.message, status: 'error', duration: 5000, isClosable: true });
      return;
    }

    setIsSubmitting(true);
    const { goLiveConfirmed } = goLive;
    try {
      // A collab the brand already accepted is past accepting — the accept endpoints refuse it.
      // It needs the REDEMPTION write instead, which is also where its selection is recorded and
      // where the reserve is trued up from the campaign's ceiling to what the creator chose.
      //
      // Done here rather than by handing off to the scheduling page. That page asks again for the
      // location and date this wizard has just collected, so the creator confirmed twice and the
      // collab was not actually settled — nor the reserve corrected — until the second one.
      if (destination === 'retail' && collab?.status !== 'pendingInfluencerApproval') {
        if (postingDate) {
          await collabApi.updateRetailCollabPostingDate(
            collabId,
            postingDate,
            selectedLocation ?? undefined
          );
        }
        await collabApi.advanceCollabToContentSubmission({
          collabId,
          skipRedeem: true,
          requireDraftApproval:
            collab?.content && Object.keys(collab.content).length > 0
              ? false
              : campaign?.requireDraftApproval,
          ...(selectedProducts.length ? { selectedProducts } : {}),
        });
        // Straight to submission, not to the confirmation screen. This branch is the REDEEM, not
        // an accept: the creator pressed "Ready to Post?", and what they came for is the upload.
        // The scheduling page's own redeem has always ended here; the wizard ending on "your
        // collab is confirmed" left them to find their way back to a collab they accepted weeks
        // ago. The accept paths below still confirm.
        // `collab` can still be loading — this branch is reachable on `collab?.status` alone — and
        // the submission path reads its content to tell UGC from deliverables. Confirm instead of
        // guessing wrong about which page to send them to.
        if (collab) {
          navigate(submissionPathForCollab(collab), { replace: true });
        } else {
          setIsConfirmed(true);
        }
        return;
      }

      if (isShopify) {
        if (!addressData?.isAddressValidated) {
          toast({
            title: 'Valid address required',
            description: 'Please choose an address from the dropdown.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        const address2 =
          `${addressData.secondaryAddressType ?? ''} ${addressData.secondaryAddressNumber ?? ''}`.trim();
        await collabApi.acceptShopifyCollab({
          collabId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          address1: addressData.streetAddress,
          address2,
          city: addressData.city,
          state: addressData.state,
          zip: addressData.zip,
          country: addressData.country,
          phone: `${countryCode}${phoneNumber.trim()}`,
          email: email.trim(),
          // The draft order works in variants; the collab records products. Both, so the accept
          // ships the right things AND leaves a selection behind.
          variantIds: selectedProducts
            .map((p) => p.variantId)
            .filter((id): id is string => typeof id === 'string'),
          selectedProducts,
          goLiveConfirmed,
        });
      } else if (destination === 'shipping') {
        if (!addressData?.isAddressValidated) {
          toast({
            title: 'Valid address required',
            description: 'Please choose an address from the dropdown.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        const requireDraftApproval =
          collab?.content && Object.keys(collab.content).length > 0
            ? false
            : campaign?.requireDraftApproval;
        await collabApi.acceptShipToHomeCollab({
          collabId,
          shippingAddress: fullAddress,
          country: addressData.country,
          state: addressData.state,
          requireDraftApproval,
          ...(selectedProducts.length ? { selectedProducts } : {}),
          goLiveConfirmed,
        });
        if (client?.email && campaign?.clientID) {
          sendAddressSubmissionEmail(
            collabId,
            client.email,
            getInfluencerPrimaryHandle(influencerData),
            campaign.clientID
          );
        }
      } else if (destination === 'retail') {
        await collabApi.acceptRetailCollab({
          collabId,
          selectedLocation,
          postingDate: postingDate as string,
          ...(selectedProducts.length ? { selectedProducts } : {}),
          goLiveConfirmed,
        });
      } else {
        await collabApi.acceptBrandManagedCollab(collabId, selectedProducts, goLiveConfirmed);
      }
      if (collab?.status === 'pendingInfluencerApproval') {
        acceptCollabNotification(collabId);
      }
      setIsConfirmed(true);
    } catch (error) {
      console.error('Error accepting collab:', error);
      if (goLive.handleError(error)) return;
      toast({
        title: 'Error accepting collab',
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!collabId || !collab || !campaign) {
    return (
      <Box px="20px" py="24px" maxW={{ base: '560px', lg: '720px' }} mx="auto" fontFamily="display">
        <Text fontSize="15px" color="#55555A">
          We couldn't load this collaboration. Go back and try again.
        </Text>
      </Box>
    );
  }

  // Confirmation — full takeover, no wizard chrome, exactly as the in-store flow does it.
  if (isConfirmed) {
    return (
      <Flex direction="column" minH="100svh" bg="#C6F432">
        <Box w="100%" flex={1}>
          <CollabConfirmedScreen
            title="Collab confirmed"
            brandName={client?.accountName ?? 'Your brand'}
            brandLogoUrl={client?.instagramProfilePicture}
            creatorAvatarUrl={influencerData?.profile_pic_url}
            connectorLabel="Your Picks"
            subtext={
              needsAddress
                ? 'Your collaboration is confirmed and your products are on their way.'
                : "Your collaboration is confirmed. We can't wait to see your content!"
            }
            onViewCollab={() =>
              navigate(`/collabs/collab-details/${collabId}`, { replace: true })
            }
            onHome={() => navigate('/')}
          >
            <ProductSelectionSummary
              products={selectedProducts}
              totalUsd={runningTotal}
              formatValue={formatUsd}
              {...summaryDestination}
            />
          </CollabConfirmedScreen>
        </Box>
      </Flex>
    );
  }

  const brandName = client?.accountName ?? 'Your brand';
  const stepName = isReviewStep ? 'Review' : isAddressStep ? 'Shipping' : isScheduleStep ? 'Store and date' : 'Products';
  const typeLine = isShopify || destination === 'shipping' ? 'Shipped to you' : needsSchedule ? 'Retail' : 'Product collab';
  const pickedValue = Math.round(runningTotal);
  const pickedValueLabel = `$${pickedValue}`;

  return (
    <Flex direction="column" minH="100svh" bg="white" fontFamily="display" color={wizardTokens.ink}>
      {/* Header is full-bleed, matching the in-store wizard; only the step content is capped. */}
      <Box w="100%">
        <WizardHeader
          stepIndex={stepIndex}
          progressCount={totalSteps}
          title={stepName}
          onBack={goBack}
          onSupport={() => navigate('/support')}
        />
      </Box>

      <Box w="100%" maxW={{ base: '560px', lg: '720px' }} mx="auto" flex={1} display="flex" flexDirection="column">
        {/* Context card (board): brand, collab type, running product value (hidden at $0). */}
        <Flex m="14px 20px 0 20px" p="12px" borderRadius="16px" bg="#F2F2F0" align="center" gap="12px">
          <Flex flex="none" w="44px" h="44px" borderRadius="12px" bg="#D8DDE6" align="center" justify="center" overflow="hidden" fontSize="14px" fontWeight={600}>
            {client?.instagramProfilePicture ? (
              <Image src={client.instagramProfilePicture} alt="" w="100%" h="100%" objectFit="cover" />
            ) : (
              brandName.trim().slice(0, 2).toUpperCase()
            )}
          </Flex>
          <Flex flex={1} minW={0} direction="column" gap="2px">
            <Text fontSize="16px" fontWeight={600} lineHeight="normal" noOfLines={1}>{brandName}</Text>
            <Text fontSize="13px" color="#55555A" lineHeight="normal" noOfLines={1}>{typeLine}{campaign.name ? ` · ${campaign.name}` : ''}</Text>
          </Flex>
          {pickedValue > 0 && (
            <Flex flex="none" direction="column" align="flex-end">
              <Text textStyle="display" fontSize={pickedValueLabel.length > 5 ? '20px' : '26px'} lineHeight={1.12} textTransform="none">{pickedValueLabel}</Text>
              <Text fontSize="10px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A">Product</Text>
            </Flex>
          )}
        </Flex>

        <VStack spacing="20px" align="stretch" flex={1} p="22px 20px 24px 20px">
          <VStack spacing="16px" align="stretch" w="100%">
            <VStack spacing="8px" align="stretch">
              <Text {...stepTitleProps}>
                {isReviewStep
                  ? 'Review your picks'
                  : isAddressStep
                    ? 'Where should we send it?'
                    : isScheduleStep
                      ? 'Where and when?'
                      : offersChoice
                        ? pickLimit === 1
                          ? 'Pick your product'
                          : `Pick ${pickLimit} products`
                        : needsVariantChoice
                          ? 'Choose your options'
                          : "What you're getting"}
              </Text>
              <Text m={0} fontSize="15px" lineHeight="1.4" color="#55555A">
                {isReviewStep
                  ? 'Check it over, then confirm to accept this collaboration.'
                  : isAddressStep
                    ? `${client?.accountName ?? 'The brand'} will send your products to this address.`
                    : isScheduleStep
                      ? `Tell us where you will pick up and when you plan to ${isDeliveryOnlyUgc(collab, campaign) ? 'deliver' : 'post'}.`
                      : offersChoice
                        ? pickLimit === 1
                          ? `Choose one of these ${optionalProducts.length}.`
                          : `Choose ${pickLimit} of these ${optionalProducts.length}.`
                        : needsVariantChoice
                          ? 'Every product below is yours — say which option you want for each.'
                          : 'Everything below comes with this collaboration.'}
              </Text>
            </VStack>

            {isScheduleStep ? (
              <VStack spacing="24px" align="stretch">
                {/* No locations and not still loading: the campaign has none configured, and the
                    accept requires one. Say so, rather than leaving Continue disabled with nothing
                    on screen to explain why. */}
                {!retailLocationsLoading && retailLocations.length === 0 && (
                  <Box p="18px" borderRadius="18px" bg="#F2F2F0">
                    <Text fontSize="16px" fontWeight={600} mb="4px">
                      No pickup location available
                    </Text>
                    <Text fontSize="14px" lineHeight="1.4" color="#3E3E42">
                      This campaign has no pickup location set up yet, so it can't be accepted
                      right now. Message the brand and they can add one.
                    </Text>
                  </Box>
                )}

                {retailLocations.length > 0 && (
                  <Box>
                    <Text as="h2" m={0} mb="10px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#66666A">
                      Where you'll pick up
                    </Text>
                    <LocationSearchBox
                      locations={retailLocations}
                      locationSearchQuery={locationSearchQuery}
                      selectedLocationId={selectedLocation ?? undefined}
                      onLocationSearch={setLocationSearchQuery}
                      onLocationSelect={(location) => {
                        setSelectedLocation(location.docId);
                        setLocationSearchQuery(location.fullAddress);
                      }}
                      isLoading={retailLocationsLoading}
                    />
                  </Box>
                )}

                <Box>
                  <Text as="h2" m={0} mb="4px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#66666A">
                    When you plan to {isDeliveryOnlyUgc(collab, campaign) ? 'deliver' : 'post'}
                  </Text>
                  <Text fontSize="13px" color="#55555A" mb="10px">
                    Pick a date that works — you can still change it later.
                  </Text>
                  <PostingDateSelector
                    scheduledPostingDate={postingDate}
                    onDateChange={(date: Date) => setPostingDate(formatDateToYYYYMMDD(date))}
                    campaign={campaign}
                    collab={collab}
                    usePostingDateLimits
                  />
                </Box>
              </VStack>
            ) : isAddressStep ? (
              <Box>
                <Text as="h2" m={0} mb="4px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#66666A">
                  Shipping address
                </Text>
                <Text fontSize="13px" color="#55555A" mb="14px">
                  Start typing and pick your address from the list.
                </Text>
                <AddressFormFields
                  initialAddress={addressData?.address}
                  initialStreetAddress={addressData?.streetAddress}
                  initialCity={addressData?.city}
                  initialState={addressData?.state}
                  initialZip={addressData?.zip}
                  initialCountry={addressData?.country}
                  initialSecondaryAddressType={addressData?.secondaryAddressType}
                  initialSecondaryAddressNumber={addressData?.secondaryAddressNumber}
                  initialIsAddressValidated={addressData?.isAddressValidated}
                  onAddressChange={setAddressData}
                />

                {/* Shopify's draft order names a recipient; ship-to-home takes it from the
                    profile. Prefilled from the creator's, and editable — the address may not be
                    theirs. */}
                {isShopify && (
                  <VStack align="stretch" spacing="14px" mt="22px">
                    <Text as="h2" m={0} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#66666A">
                      Who is it going to?
                    </Text>
                    <HStack spacing="10px" align="flex-start">
                      <Input
                        {...FIELD}
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                      <Input
                        {...FIELD}
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </HStack>
                    <HStack spacing="10px" align="flex-start">
                      <Input
                        {...FIELD}
                        w="88px"
                        flexShrink={0}
                        placeholder="+1"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        aria-label="Country calling code"
                      />
                      <Input
                        {...FIELD}
                        type="tel"
                        placeholder="Phone number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                    </HStack>
                    <Input
                      {...FIELD}
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </VStack>
                )}
              </Box>
            ) : isProductStep ? (
              <Box>
                {offersChoice && (
                  <Flex align="center" justify="space-between" mb="12px" gap="10px">
                    <Text as="h2" m={0} {...eyebrowProps} fontSize="13px" letterSpacing="0.08em">
                      {picked.length} of {pickLimit} picked
                    </Text>
                    {atLimit && (
                      <Flex as="span" h="28px" px="10px" borderRadius="999px" bg={wizardTokens.ink} color="#C6F432" align="center" fontSize="12px" fontWeight={600}>
                        Limit reached
                      </Flex>
                    )}
                  </Flex>
                )}
                {offersChoice && (
                  <SimpleGrid columns={{ base: 2, md: 3 }} spacing="12px" alignItems="start">
                    {optionalProducts.map((product) => (
                      <ProductPickerCard
                        key={product.id}
                        product={product}
                        chosenVariantId={variantByProduct[product.id]}
                        isSelected={picked.includes(product.id)}
                        isExpanded={expandedProductId === product.id}
                        onToggleExpanded={() =>
                          setExpandedProductId((prev) => (prev === product.id ? null : product.id))
                        }
                        onToggleProduct={() => toggle(product.id)}
                        onChooseVariant={(variantId) => chooseVariant(product.id, variantId)}
                      />
                    ))}
                  </SimpleGrid>
                )}

                {/* Products the creator receives whatever they pick. One that comes in options
                    still needs them to say which, so it gets a card rather than a line. When
                    there is no choice BETWEEN products this is the whole offer. */}
                {includedProducts.some((p) => getVariantChoices(p).length > 0) ? (
                  <Box mt={offersChoice ? '20px' : 0}>
                    {offersChoice && (
                      <Text as="h2" m={0} mb="10px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" color="#66666A">
                        INCLUDED FOR YOU EITHER WAY
                      </Text>
                    )}
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing="12px" alignItems="start">
                      {includedProducts.map((product) => (
                        <ProductPickerCard
                          key={product.id}
                          product={product}
                          chosenVariantId={variantByProduct[product.id]}
                          isSelected
                          isExpanded={expandedProductId === product.id}
                          onToggleExpanded={() =>
                            setExpandedProductId((prev) =>
                              prev === product.id ? null : product.id
                            )
                          }
                          onToggleProduct={() => undefined}
                          onChooseVariant={(variantId) => chooseVariant(product.id, variantId)}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                ) : (
                  <IncludedAnyway
                    products={includedProducts}
                    variantByProduct={variantByProduct}
                  />
                )}

                {atLimit && (
                  <HStack spacing="10px" mt="14px" p="12px 14px" borderRadius="14px" bg="#F2F2F0">
                    <Text fontSize="14px" lineHeight="1.4" color="#3E3E42">
                      {pickLimit === 1
                        ? "That's your pick — choosing another swaps it."
                        : `That's ${pickLimit} — choosing another swaps your earliest pick.`}
                    </Text>
                  </HStack>
                )}
              </Box>
            ) : (
              <ProductSelectionSummary
                products={selectedProducts}
                totalUsd={runningTotal}
                formatValue={formatUsd}
                {...summaryDestination}
                // Nothing to edit when the campaign made the choice for them.
                onEditProducts={hasProductStep ? () => setStepIndex(0) : undefined}
                onEditAddress={
                  needsAddress
                    ? () => setStepIndex(addressStepIndex)
                    : needsSchedule
                      ? () => setStepIndex(scheduleStepIndex)
                      : undefined
                }
              />
            )}
            {isReviewStep && (goLive.changed || goLive.blockedReason) && <PostByConfirm state={goLive} />}
          </VStack>
        </VStack>
      </Box>

      <WizardBottomBar
        label={isReviewStep ? 'Confirm' : 'Continue'}
        loadingText="Confirming…"
        isDisabled={!canAdvance}
        isLoading={isSubmitting}
        onClick={() => (isReviewStep ? submit() : setStepIndex((i) => i + 1))}
        secondary={
          selectedProducts.length > 0 ? (
            <Text fontSize="13px" color="#55555A" textAlign="center" mt="8px">
              {selectedProducts.length} item{selectedProducts.length === 1 ? '' : 's'} ·{' '}
              {formatUsd(runningTotal)}
            </Text>
          ) : undefined
        }
      />
    </Flex>
  );
};

export default SelectProductsPage;
